// frontend/src/components/Dashboard.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Alert,
    Divider,
    Popover,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Stack,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    LinearProgress,
    useMediaQuery,

} from '@mui/material';
import useDocumentTitle from '../hooks/useDocumentTitle'; // Import the custom hook
import StorageIcon from '@mui/icons-material/Storage';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CancelIcon from '@mui/icons-material/Cancel';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AlarmIcon from '@mui/icons-material/Alarm';
import WarningIcon from '@mui/icons-material/Warning'; // Added for blinking icon
import Layout from './Layout';
import {
    getDashboardSummary,
    getRequiredDispatchTodayOrdersExcludeExOrders,
    getReShipOrdersToday,
} from '../services/api';
import LoadingIndicator from './LoadingIndicator';
import IssueDialog from './IssueDialog';
import SummaryCardWithAreaChart from './Cards/SummaryCardWithAreaChart';
import SummaryCardWithPieChart from './Cards/SummaryCardWithPieChart';
import RequiredDispatchProgressCard from './Cards/RequiredDispatchProgressCard';
import { format, subDays } from 'date-fns';
import { useTheme, styled, alpha } from '@mui/material/styles';
import { getColorByPercentage, getDispatchColor } from '../utils/colorUtils';
import ClientTable from './ClientTable'; // Ensure ClientTable is updated separately
import CancelOrderDialog from './CancelOrderDialog';
import DispatchDialog from './DispatchDialog'; // Or your dispatch dialog component name


// Styled component for blinking attention icon
const BlinkingWarningIcon = styled(WarningIcon)(({ theme }) => ({
    color: theme.palette.error.main,
    animation: 'blinker 1s linear infinite',
    '@keyframes blinker': {
        '50%': { opacity: 0 },
    },
    marginLeft: theme.spacing(0.5),
}));

const ACTIVE_ISSUE_STATUSES = ['ON HOLD', 'IN PROGRESS', 'URGENT', 'ERROR'];

const hasActiveIssue = (status) =>
    ACTIVE_ISSUE_STATUSES.includes(String(status || '').trim().toUpperCase());

const Dashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const stickyKpiTop = isMobile ? 64 : 72;

    // State Variables
    const [ordersData, setOrdersData] = useState({ summary: {}, clients: [] });
    const [loading, setLoading] = useState(true);
    const [dateTime, setDateTime] = useState('');
    const [intervalTime, setIntervalTime] = useState(15000); // Default 15s interval
    const [error, setError] = useState(null); // For error handling
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedClientName, setSelectedClientName] = useState('');
    const [dispatchOrders, setDispatchOrders] = useState([]);
    const [dispatchLoading, setDispatchLoading] = useState(false);
    const [dispatchError, setDispatchError] = useState(null);
    const [openIssueDialog, setOpenIssueDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);

    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [openDispatchedDialog, setOpenDispatchedDialog] = useState(false);
    const [reShipModalOpen, setReShipModalOpen] = useState(false);
    const [selectedClientNameForReShip, setSelectedClientNameForReShip] = useState('');
    const [reShipOrders, setReShipOrders] = useState([]);
    const [reShipLoading, setReShipLoading] = useState(false);
    const [reShipError, setReShipError] = useState(null);
    const [reShipTabIndex, setReShipTabIndex] = useState(0);
    const [isKpiStripStuck, setIsKpiStripStuck] = useState(false);


    // Add a ref for the main container
    const mainContainerRef = useRef(null);
    const kpiStickySentinelRef = useRef(null);

    // Additional State for Cutoff Times Popover
    const [cutoffPopoverAnchorEl, setCutoffPopoverAnchorEl] = useState(null);
    const [cutoffClient, setCutoffClient] = useState(null);

    const buildQueueMeta = (orders = []) => {
        const total = orders.length;
        const issueCount = orders.filter((order) => hasActiveIssue(order.issueStatus)).length;

        return {
            total,
            issueCount,
            clearCount: Math.max(total - issueCount, 0),
        };
    };

    const dispatchQueueMeta = buildQueueMeta(dispatchOrders);
    const reShipQueueMeta = buildQueueMeta(reShipOrders);

    // Interval Options for Reload
    const intervalOptions = [
        { label: '5 seconds', value: 5000 },
        { label: '15 seconds', value: 15000 },
        { label: '1 minute', value: 60000 },
        { label: '5 minutes', value: 300000 },
        { label: '10 minutes', value: 600000 },
        { label: '30 minutes', value: 1800000 },
        { label: '1 hour', value: 3600000 },
    ];

    // State and Effect for Toggling Document Title
    const [titleToggle, setTitleToggle] = useState(true); // true: "IV Linx", false: "X RDO"

    useEffect(() => {
        // Toggle the title every 5 seconds
        const titleInterval = setInterval(() => {
            setTitleToggle((prev) => !prev);
        }, 5000); // 5000ms = 5 seconds

        // Cleanup interval on component unmount
        return () => clearInterval(titleInterval);
    }, []);

    // Set the document title using the custom hook
    useDocumentTitle(
        titleToggle
            ? 'IV Linx'
            : `${ordersData.summary.requiredDispatchedToday || 0} RDO`
    );

    // Update Current Date and Time Every Second
    useEffect(() => {
        const updateDateTime = () => {
            const today = new Date();
            const options = {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            };
            setDateTime(today.toLocaleDateString('en-UK', options));
        };

        updateDateTime();
        const intervalId = setInterval(updateDateTime, 1000);
        return () => clearInterval(intervalId);
    }, []);

    // Fetch Orders Data from Backend
    const fetchOrdersData = useCallback(async () => {
        try {
            const response = await getDashboardSummary();
            // Keep a stable object shape so render code never crashes on partial payloads.
            const safeResponse = {
                summary: {
                    ...(response?.summary || {}),
                    newOrdersLast7Days: Array.isArray(response?.summary?.newOrdersLast7Days)
                        ? response.summary.newOrdersLast7Days
                        : [],
                    dispatchedLast7Days: Array.isArray(response?.summary?.dispatchedLast7Days)
                        ? response.summary.dispatchedLast7Days
                        : [],
                    cancelledLast7Days: Array.isArray(response?.summary?.cancelledLast7Days)
                        ? response.summary.cancelledLast7Days
                        : [],
                },
                clients: Array.isArray(response?.clients) ? response.clients : [],
            };
            setOrdersData(safeResponse);
            setError(null); // Reset error on successful fetch

        } catch (err) {
            console.error('Error fetching orders data:', err);
            setError('Failed to fetch dashboard data. Please try again later.');
            setOrdersData((prev) => ({
                summary: {
                    ...(prev?.summary || {}),
                    newOrdersLast7Days: Array.isArray(prev?.summary?.newOrdersLast7Days)
                        ? prev.summary.newOrdersLast7Days
                        : [],
                    dispatchedLast7Days: Array.isArray(prev?.summary?.dispatchedLast7Days)
                        ? prev.summary.dispatchedLast7Days
                        : [],
                    cancelledLast7Days: Array.isArray(prev?.summary?.cancelledLast7Days)
                        ? prev.summary.cancelledLast7Days
                        : [],
                },
                clients: Array.isArray(prev?.clients) ? prev.clients : [],
            }));
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial Data Fetch
    useEffect(() => {
        fetchOrdersData();
    }, [fetchOrdersData]);

    // Manage Reload Based on Selected Interval
    useEffect(() => {
        const intervalId = setInterval(() => {
            fetchOrdersData();
        }, intervalTime);

        // Cleanup on Unmount or when intervalTime changes
        return () => clearInterval(intervalId);
    }, [fetchOrdersData, intervalTime]);

    // Detect when sticky KPI strip is pinned, so we can add a subtle bottom shadow.
    useEffect(() => {
        if (!kpiStickySentinelRef.current || typeof IntersectionObserver === 'undefined') return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsKpiStripStuck(!entry.isIntersecting);
            },
            {
                root: null,
                threshold: 0,
                rootMargin: `-${stickyKpiTop}px 0px 0px 0px`,
            }
        );

        observer.observe(kpiStickySentinelRef.current);
        return () => observer.disconnect();
    }, [stickyKpiTop]);


    // Load Dispatch Orders
    const loadDispatchOrders = useCallback(async () => {
        if (!selectedClientName) return;

        setDispatchLoading(true);
        setDispatchError(null);

        try {
            const orders = await getRequiredDispatchTodayOrdersExcludeExOrders(selectedClientName);
            // Sort the orders by createdAt in ascending order
            const sortedOrders = orders.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            setDispatchOrders(sortedOrders);
        } catch (error) {
            setDispatchError('Failed to load orders. Please try again.');
            console.error(`Error fetching required dispatch today orders for ${selectedClientName}:`, error);
        } finally {
            setDispatchLoading(false);
        }
    }, [selectedClientName]);

    useEffect(() => {
        if (modalOpen && selectedClientName) {
            loadDispatchOrders();
        }
    }, [modalOpen, selectedClientName, loadDispatchOrders]);


    // Dialog openers (pass as props):
    const handleOpenIssueDialog = (order) => {
        setSelectedOrder(order);
        setOpenIssueDialog(true);
    };
    const handleOpenCancelDialog = (order) => {
        setSelectedOrder(order);
        setOpenCancelDialog(true);
    };
    const handleOpenDispatchedDialog = (order) => {
        setSelectedOrder(order);
        setOpenDispatchedDialog(true);
    };

    const handleCloseAllDialogs = () => {
        setOpenIssueDialog(false);
        setOpenCancelDialog(false);
        setOpenDispatchedDialog(false);
        setSelectedOrder(null);
    };

    const handleDispatchTodayClick = (clientName) => {
        setTabIndex(0);
        setSelectedClientName(clientName);
        setModalOpen(true);
    };


    // Handle Cutoff Time Icon Click
    const handleCutoffTimeClick = (event, client) => {
        setCutoffPopoverAnchorEl(event.currentTarget);
        setCutoffClient(client);
    };

    const handleCutoffPopoverClose = () => {
        setCutoffPopoverAnchorEl(null);
        setCutoffClient(null);
    };

    const openCutoffPopover = Boolean(cutoffPopoverAnchorEl);

    // Handle modal close and set focus back to main container
    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedClientName('');
        setDispatchOrders([]);
        setDispatchError(null);

        // Set focus back to the main container
        if (mainContainerRef.current) {
            mainContainerRef.current.focus();
        }
    };

    // Open Re-Ship Modal, fetch orders
    const handleOpenReShipModal = async (clientName) => {
        setSelectedClientNameForReShip(clientName);
        setReShipModalOpen(true);
        setReShipLoading(true);
        setReShipError(null);
        setReShipTabIndex(0);
        try {
            const orders = await getReShipOrdersToday(clientName);
            setReShipOrders(orders);
        } catch (error) {
            setReShipError('Failed to load Re-Ship Orders. Please try again.');
        } finally {
            setReShipLoading(false);
        }
    };

    // Close Re-Ship Modal
    const handleCloseReShipModal = () => {
        setReShipModalOpen(false);
        setSelectedClientNameForReShip('');
        setReShipOrders([]);
        setReShipTabIndex(0);
    };

    // If Data is Loading, Show Loading Indicator
    if (loading) {
        return (
            <Layout>
                <LoadingIndicator />
            </Layout>
        );
    }

    // Prepare Summary Data for Cards
    const { summary, clients } = ordersData;
    const selectedIntervalLabel = intervalOptions.find((option) => option.value === intervalTime)?.label || '15 seconds';
    const topKpiStrips = [
        {
            label: 'Fulfilment Accuracy',
            value: `${Number(summary.fulfilmentAccuracyRate || 0).toFixed(2)}%`,
            color: theme.palette.dispatch.green,
        },
        {
            label: 'Total New Orders',
            value: summary.newOrdersTotal || 0,
            color: theme.palette.primary.main,
        },
        {
            label: 'Dispatched',
            value: summary.dispatched || 0,
            color: theme.palette.dispatch.green,
        },
        {
            label: 'Required Dispatch',
            value: summary.requiredDispatchedToday || 0,
            color: theme.palette.dispatch.orange,
        },
        {
            label: 'Cancelled Orders',
            value: summary.cancelledOrdersToday || 0,
            color: theme.palette.dispatch.red,
        },
    ];
    const denseSnapshotCardSx = {
        minHeight: 136,
    };

    // Prepare Data for TinyAreaCharts
    const prepareTinyAreaChartData = (dataArray) => {
        if (!Array.isArray(dataArray)) return [];
        // Generate dates for the last 7 days including today
        const today = new Date();
        const last7Days = Array.from({ length: 7 }, (_, index) =>
            subDays(today, 6 - index)
        ); // Array of Date objects from 6 days ago to today

        return dataArray.map((value, index) => ({
            date: format(last7Days[index], 'dd MMM'), // e.g., '26 Oct'
            value,
        }));
    };

    // Function to format cutoff time
    const formatCutoffTime = (time) => {
        if (time) {
            // Append 'Z' to indicate UTC time if not already present
            const utcTime = time.endsWith('Z') ? time : `${time}Z`;
            const date = new Date(utcTime);
            if (!isNaN(date)) {
                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
            }
        }
        return 'N/A';
    };

    return (
        <Layout>
            <Box
                ref={mainContainerRef}
                tabIndex="-1" // Make the Box focusable
                sx={{
                    p: { xs: 2, sm: 3 },
                    background: 'linear-gradient(180deg, #f3f6fb 0%, #eef3f8 100%)',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 1, sm: 1.25 },
                        mb: 1.5,
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        background: 'linear-gradient(90deg, #ffffff 0%, #f8fbff 100%)',
                    }}
                >
                    <Stack spacing={1}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ md: 'flex-start' }}
                            spacing={1}
                        >
                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{ color: 'text.secondary', letterSpacing: 0.9, lineHeight: 1.1 }}
                                >
                                    Operations Control
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.15, mt: 0.25 }}
                                >
                                    Fulfilment KPI Dashboard
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 0.4, color: 'text.secondary', display: 'block' }}>
                                    Live dispatch, backlog, and client performance overview.
                                </Typography>
                            </Box>

                            <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, maxWidth: 220 }}>
                                <InputLabel id="reload-interval-label">Auto refresh</InputLabel>
                                <Select
                                    labelId="reload-interval-label"
                                    id="reload-interval"
                                    value={intervalTime}
                                    onChange={(e) => setIntervalTime(e.target.value)}
                                    label="Auto refresh"
                                >
                                    {intervalOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`Current time ${dateTime}`}
                                sx={{ borderColor: 'divider' }}
                            />
                            <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={`Refresh every ${selectedIntervalLabel}`}
                            />
                        </Stack>
                    </Stack>
                </Paper>

                {/* Display Error Message if Exists */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        mb: 3,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: '#fff',
                    }}
                >
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Client Workboard
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Live operations board for dispatch queues, issue visibility, re-ship risk, and client progress.
                        </Typography>
                    </Box>
                    <Box ref={kpiStickySentinelRef} sx={{ height: 1, mt: -0.5 }} />
                    <Paper
                        elevation={2}
                        sx={{
                            position: 'sticky',
                            top: `${stickyKpiTop}px`,
                            zIndex: (muiTheme) => muiTheme.zIndex.appBar - 1,
                            p: 0.75,
                            mb: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'rgba(248, 250, 252, 0.94)',
                            backdropFilter: 'blur(4px)',
                            boxShadow: 'none',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: -1,
                                height: '1px',
                                boxShadow: isKpiStripStuck ? '0 2px 6px rgba(15, 23, 42, 0.2)' : 'none',
                                opacity: isKpiStripStuck ? 1 : 0,
                                transition: 'opacity 0.16s ease',
                                pointerEvents: 'none',
                            },
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', px: 0.5, pb: 0.75, textTransform: 'uppercase', letterSpacing: 0.35 }}
                        >
                            Live queue totals
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(5, minmax(0, 1fr))',
                                },
                                gap: 1,
                            }}
                        >
                            {topKpiStrips.map((kpi) => (
                                <Paper
                                    key={kpi.label}
                                    elevation={0}
                                    sx={{
                                        px: 1,
                                        py: 0.875,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: alpha(kpi.color, 0.18),
                                        backgroundColor: alpha(kpi.color, 0.04),
                                        borderLeft: `4px solid ${kpi.color}`,
                                        minHeight: 64,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: 0.35 }}
                                    >
                                        {kpi.label}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 800,
                                            color: kpi.color,
                                            lineHeight: 1.15,
                                            mt: 0.25,
                                            fontSize: { xs: '1.15rem', sm: '1.3rem' },
                                        }}
                                    >
                                        {kpi.value}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                    </Paper>
                    {isMobile ? (
                        <Grid container spacing={2}>
                            {clients.map((client) => {
                                const displayProgress =
                                    (client.requiredDispatchToday === 0 && client.dispatchedOrders === 0)
                                        ? 100
                                        : (client.progressPercentage > 100 ? 100 : client.progressPercentage || 0);
                                const issueCount = Number(client.issueOrdersToday || 0);
                                const reshipCount = Number(client.reshipOrdersToday || 0);
                                const requiredDispatchCount = Number(client.requiredDispatchToday || 0);
                                const dispatchedCount = Number(client.dispatchedOrders || 0);
                                const needsAttention = issueCount > 0 || reshipCount > 0;
                                const dynamicColor = getColorByPercentage(displayProgress, theme.palette.dispatch);
                                const dispatchColor = getDispatchColor(requiredDispatchCount, theme.palette.dispatch);

                                return (
                                    <Grid item xs={12} key={client.clientName}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.75,
                                                borderRadius: 2.5,
                                                border: '1px solid',
                                                borderColor: needsAttention ? alpha(theme.palette.warning.main, 0.35) : 'divider',
                                                backgroundColor: needsAttention
                                                    ? alpha(theme.palette.warning.main, 0.05)
                                                    : '#fafbfd',
                                            }}
                                        >
                                            <Stack spacing={1.25}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontSize: '1rem', fontWeight: 700 }}>
                                                            {client.clientName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {needsAttention ? 'Exceptions need review' : 'Queue clear, keep monitoring'}
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleCutoffTimeClick(e, client)}
                                                        aria-label={`View cutoff times for ${client.clientName}`}
                                                        sx={{ mt: -0.25 }}
                                                    >
                                                        <AlarmIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>

                                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                                    <Chip
                                                        size="small"
                                                        label={needsAttention ? 'Attention needed' : 'Clear'}
                                                        color={needsAttention ? 'warning' : 'success'}
                                                        variant={needsAttention ? 'filled' : 'outlined'}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        label={`Issues ${issueCount}`}
                                                        color={issueCount > 0 ? 'warning' : 'default'}
                                                        variant={issueCount > 0 ? 'filled' : 'outlined'}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        label={`Re-ship ${reshipCount}`}
                                                        color={reshipCount > 0 ? 'error' : 'default'}
                                                        variant={reshipCount > 0 ? 'filled' : 'outlined'}
                                                    />
                                                </Stack>

                                                <Button
                                                    fullWidth
                                                    onClick={() => handleDispatchTodayClick(client.clientName)}
                                                    aria-label={`View Required Dispatch Today Orders for ${client.clientName}`}
                                                    sx={{
                                                        justifyContent: 'space-between',
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        px: 1.25,
                                                        py: 1,
                                                        borderRadius: 2,
                                                        border: '1px solid',
                                                        borderColor: alpha(dispatchColor, 0.22),
                                                        backgroundColor: alpha(dispatchColor, 0.07),
                                                        color: theme.palette.text.primary,
                                                    }}
                                                >
                                                    <Box sx={{ textAlign: 'left' }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                                                            Dispatch queue
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                            Open required orders
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ color: dispatchColor, fontWeight: 800 }}>
                                                        {requiredDispatchCount}
                                                    </Typography>
                                                </Button>

                                                {reshipCount > 0 && (
                                                    <Button
                                                        fullWidth
                                                        onClick={() => handleOpenReShipModal(client.clientName)}
                                                        aria-label={`View Re-Ship Orders Today for ${client.clientName}`}
                                                        sx={{
                                                            justifyContent: 'space-between',
                                                            textTransform: 'none',
                                                            fontWeight: 700,
                                                            px: 1.25,
                                                            py: 0.9,
                                                            borderRadius: 2,
                                                            border: '1px solid',
                                                            borderColor: alpha(theme.palette.error.main, 0.22),
                                                            backgroundColor: alpha(theme.palette.error.main, 0.05),
                                                            color: theme.palette.text.primary,
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                Re-ship queue
                                                            </Typography>
                                                            <BlinkingWarningIcon fontSize="small" />
                                                        </Box>
                                                        <Typography variant="body1" sx={{ color: theme.palette.error.main, fontWeight: 800 }}>
                                                            {reshipCount}
                                                        </Typography>
                                                    </Button>
                                                )}

                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="caption" color="text.secondary">
                                                        Dispatched today
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: theme.palette.dispatch.green, fontWeight: 700 }}>
                                                        {dispatchedCount}
                                                    </Typography>
                                                </Stack>

                                                <LinearProgress
                                                    variant="determinate"
                                                    value={displayProgress}
                                                    sx={{
                                                        height: 8,
                                                        borderRadius: 5,
                                                        [`& .MuiLinearProgress-bar`]: {
                                                            backgroundColor: dynamicColor,
                                                            transition: 'background-color 0.3s ease',
                                                        },
                                                        transition: 'background-color 0.3s ease',
                                                    }}
                                                />
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="caption" color="text.secondary">
                                                        Progress
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: dynamicColor }}>
                                                        {displayProgress}%
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ) : (
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                            <ClientTable
                                clients={clients}
                                onDispatchTodayClick={handleDispatchTodayClick}
                                onReShipOrdersClick={handleOpenReShipModal}
                                onCutoffTimeClick={handleCutoffTimeClick}
                                openIssueDialog={handleOpenIssueDialog}
                                openCancelDialog={handleOpenCancelDialog}
                                openDispatchedDialog={handleOpenDispatchedDialog}
                                currentClientName={selectedClientName}
                                reloadOrders={loadDispatchOrders}
                            />
                        </Box>
                    )}
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 1.25, sm: 1.5 },
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: '#fff',
                    }}
                >
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Trend Snapshot
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Secondary context for volume and throughput. Use the workboard above for live exception handling.
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(5, minmax(0, 1fr))',
                            },
                            gap: 0.75,
                        }}
                    >
                        <SummaryCardWithPieChart
                            icon={AssessmentIcon}
                            label="Fulfilment Accuracy"
                            percentage={summary.fulfilmentAccuracyRate || 0}
                            color={theme.palette.dispatch.green}
                            tooltip="Share of dispatched orders fulfilled correctly without errors or delay."
                            compact
                            sx={denseSnapshotCardSx}
                        />
                        <SummaryCardWithAreaChart
                            icon={StorageIcon}
                            label="New Orders"
                            value={summary.newOrdersTotal || 0}
                            color={theme.palette.primary.main}
                            tooltip="Total new orders received today."
                            percentageChange={summary.newOrdersPercentageChange}
                            chartData={prepareTinyAreaChartData(summary.newOrdersLast7Days)}
                            badge="Today"
                            compact
                            sx={denseSnapshotCardSx}
                        />
                        <SummaryCardWithAreaChart
                            icon={LocalShippingIcon}
                            label="Dispatched"
                            value={summary.dispatched || 0}
                            color={theme.palette.dispatch.green}
                            tooltip="Total orders dispatched today."
                            percentageChange={summary.dispatchedPercentageChange}
                            chartData={prepareTinyAreaChartData(summary.dispatchedLast7Days)}
                            badge="Today"
                            compact
                            sx={denseSnapshotCardSx}
                        />
                        <RequiredDispatchProgressCard
                            icon={PriorityHighIcon}
                            label="Required Dispatch"
                            requiredDispatch={summary.requiredDispatchedToday || 0}
                            reshipOrders={summary.reshipOrdersToday || 0}
                            progressPercentage={summary.progressPercentage || 0}
                            tooltip="Overall progress against the current dispatch queue."
                            badge="Today"
                            compact
                            sx={denseSnapshotCardSx}
                        />
                        <SummaryCardWithAreaChart
                            icon={CancelIcon}
                            label="Cancelled"
                            value={summary.cancelledOrdersToday || 0}
                            color={theme.palette.dispatch.red}
                            tooltip="Orders cancelled today."
                            percentageChange={summary.cancelledPercentageChange}
                            chartData={prepareTinyAreaChartData(summary.cancelledLast7Days)}
                            badge="Today"
                            compact
                            sx={denseSnapshotCardSx}
                        />
                    </Box>
                </Paper>

                {/* Popover for Cutoff Times */}
                <Popover
                    open={openCutoffPopover}
                    anchorEl={cutoffPopoverAnchorEl}
                    onClose={handleCutoffPopoverClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        elevation: 3,
                        sx: {
                            padding: 2,
                            maxWidth: { xs: '90%', sm: 500, md: 600 }, // Responsive widths
                            width: '100%',
                        },
                    }}
                >
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Cutoff Times for {cutoffClient?.clientName}
                        </Typography>
                        {cutoffClient?.cutoffTimes && cutoffClient.cutoffTimes.length > 0 ? (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: 500 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Shipping Carrier</TableCell>
                                            <TableCell>Shipping Method</TableCell>
                                            <TableCell>Condition</TableCell>
                                            <TableCell>Cutoff Time</TableCell>
                                            <TableCell>Day Offset</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cutoffClient.cutoffTimes.map((cutoff, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{cutoff.shipping_carrier}</TableCell>
                                                <TableCell>{cutoff.shipping_method}</TableCell>
                                                <TableCell>
                                                    {cutoff.shipping_method_condition === 'equals' ? '==' : '!='}
                                                </TableCell>
                                                <TableCell>{formatCutoffTime(cutoff.cutoff_time)}</TableCell>
                                                <TableCell>
                                                    {cutoff.cutoff_day_offset > 0 ? cutoff.cutoff_day_offset : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        ) : (
                            <Typography variant="body2">
                                No cutoff times available.
                            </Typography>
                        )}
                    </Box>
                </Popover>

                {/* Dispatch Today Modal: Table is handled INSIDE ClientTable now, just show/hide modal */}
                <Dialog
                    open={modalOpen}
                    onClose={handleModalClose}
                    fullWidth
                    maxWidth="lg"
                    fullScreen={isMobile}
                    aria-labelledby="dispatch-orders-dialog-title"
                >
                    <DialogTitle
                        id="dispatch-orders-dialog-title"
                        sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                        <Stack spacing={1}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ sm: 'flex-start' }}
                            >
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        Dispatch queue
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 700 }}>
                                        {selectedClientName}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                    <Chip size="small" color="primary" variant="outlined" label={`${dispatchQueueMeta.total} orders`} />
                                    <Chip
                                        size="small"
                                        color="warning"
                                        variant={dispatchQueueMeta.issueCount > 0 ? 'filled' : 'outlined'}
                                        label={`Issues ${dispatchQueueMeta.issueCount}`}
                                    />
                                    <Chip size="small" variant="outlined" label={`Clear ${dispatchQueueMeta.clearCount}`} />
                                </Stack>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                Review blocked rows first, then confirm dispatch inline from the table.
                            </Typography>
                        </Stack>
                    </DialogTitle>
                    <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                        <ClientTable
                            orders={dispatchOrders}
                            categories={Array.from(new Set(dispatchOrders.map(o => o.category ?? 'Uncategorised'))).sort()}
                            loading={dispatchLoading}
                            error={dispatchError}
                            tabIndex={tabIndex}
                            setTabIndex={setTabIndex}
                            openIssueDialog={handleOpenIssueDialog}
                            openCancelDialog={handleOpenCancelDialog}
                            openDispatchedDialog={handleOpenDispatchedDialog}
                            currentClientName={selectedClientName}
                            reloadOrders={loadDispatchOrders}
                            dialogMode="dispatch"
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Button onClick={handleModalClose}>Close</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={reShipModalOpen}
                    onClose={handleCloseReShipModal}
                    fullWidth
                    maxWidth="lg"
                    fullScreen={isMobile}
                    aria-labelledby="re-ship-orders-dialog-title"
                >
                    <DialogTitle
                        id="re-ship-orders-dialog-title"
                        sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                        <Stack spacing={1}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ sm: 'flex-start' }}
                            >
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        Re-ship queue
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 700 }}>
                                        {selectedClientNameForReShip}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                    <Chip size="small" color="primary" variant="outlined" label={`${reShipQueueMeta.total} orders`} />
                                    <Chip
                                        size="small"
                                        color="warning"
                                        variant={reShipQueueMeta.issueCount > 0 ? 'filled' : 'outlined'}
                                        label={`Issues ${reShipQueueMeta.issueCount}`}
                                    />
                                    <Chip size="small" variant="outlined" label={`Clear ${reShipQueueMeta.clearCount}`} />
                                </Stack>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                Review blockers first, then use the inline actions to update the issue or confirm dispatch.
                            </Typography>
                        </Stack>
                    </DialogTitle>
                    <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                        <ClientTable
                            orders={reShipOrders}
                            categories={Array.from(new Set(reShipOrders.map(o => o.category ?? 'Uncategorised'))).sort()}
                            loading={reShipLoading}
                            error={reShipError}
                            tabIndex={reShipTabIndex}
                            setTabIndex={setReShipTabIndex}
                            openIssueDialog={handleOpenIssueDialog}
                            openCancelDialog={handleOpenCancelDialog}
                            openDispatchedDialog={handleOpenDispatchedDialog}
                            currentClientName={selectedClientNameForReShip}
                            reloadOrders={() => handleOpenReShipModal(selectedClientNameForReShip)}
                            dialogMode="reship"
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Button onClick={handleCloseReShipModal} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ISSUE DIALOG */}
                {selectedOrder && openIssueDialog && (
                    <IssueDialog
                        open={openIssueDialog}
                        onClose={handleCloseAllDialogs}
                        order={selectedOrder}
                        onIssueAdded={() => {
                            handleCloseAllDialogs();
                            fetchOrdersData();
                            if (modalOpen && selectedClientName) loadDispatchOrders();
                            if (reShipModalOpen && selectedClientNameForReShip) handleOpenReShipModal(selectedClientNameForReShip);
                        }}
                    />
                )}
                {/* CANCEL DIALOG */}
                {selectedOrder && openCancelDialog && (
                    <CancelOrderDialog
                        open={openCancelDialog}
                        onClose={handleCloseAllDialogs}
                        order={selectedOrder}
                        onCancelled={() => {
                            handleCloseAllDialogs();
                            fetchOrdersData();
                            if (modalOpen && selectedClientName) loadDispatchOrders();
                            if (reShipModalOpen && selectedClientNameForReShip) handleOpenReShipModal(selectedClientNameForReShip);
                        }}
                    />
                )}
                {/* DISPATCHED DIALOG */}
                {selectedOrder && openDispatchedDialog && (
                    <DispatchDialog
                        open={openDispatchedDialog}
                        onClose={handleCloseAllDialogs}
                        order={selectedOrder}
                        onDispatched={() => {
                            handleCloseAllDialogs();
                            fetchOrdersData();
                            if (modalOpen && selectedClientName) loadDispatchOrders();
                            if (reShipModalOpen && selectedClientNameForReShip) handleOpenReShipModal(selectedClientNameForReShip);
                        }}
                    />
                )}
            </Box>
        </Layout>
    );
}

export default Dashboard;
